import React, { useState, useEffect } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import { useAuth } from '../../contexts/AuthContext';
import { mobileConfigService } from '../../services/mobileConfigService';
import PWASettingsTab from './components/PWASettingsTab';
import PlatformConfigTab from './components/PlatformConfigTab';
import ConfigHeader from './components/ConfigHeader';

const TABS = [
  { id: 'pwa', label: 'PWA Settings', icon: 'M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z' },
  { id: 'ios', label: 'iOS Config', icon: 'M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z' },
  { id: 'android', label: 'Android Config', icon: 'M17.523 15.3414c-.5511 0-.9993-.4486-.9993-.9997s.4483-.9993.9993-.9993c.5511 0 .9993.4483.9993.9993.0001.5511-.4482.9997-.9993.9997m-11.046 0c-.5511 0-.9993-.4486-.9993-.9997s.4482-.9993.9993-.9993c.5511 0 .9993.4483.9993.9993 0 .5511-.4483.9997-.9993.9997m11.4045-6.02l1.9973-3.4592a.416.416 0 00-.1521-.5676.416.416 0 00-.5676.1521l-2.0223 3.503C15.5902 8.2439 13.8533 7.8508 12 7.8508s-3.5902.3931-5.1367 1.0989L4.841 5.4467a.4161.4161 0 00-.5677-.1521.4157.4157 0 00-.1521.5676l1.9973 3.4592C2.6889 11.1867.3432 14.6589 0 18.761h24c-.3435-4.1021-2.6892-7.5743-6.1185-9.4396' }
];

const MobileAppConfiguration = () => {
  const { userProfile } = useAuth();
  const [activeTab, setActiveTab] = useState('pwa');
  const [configs, setConfigs] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null);

  useEffect(() => {
    loadAllConfigs();
  }, []);

  const loadAllConfigs = async () => {
    setLoading(true);
    try {
      const all = await mobileConfigService?.getAllConfigs();
      const mapped = {};
      all?.forEach((item) => {
        if (!mapped?.[item?.platform]) mapped[item.platform] = {};
        mapped[item.platform][item.config_key] = item?.config_value;
      });
      setConfigs(mapped);
    } catch (err) {
      console.error('Failed to load configs:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (platform, configKey, value) => {
    setSaving(true);
    setSaveMessage(null);
    try {
      await mobileConfigService?.upsertConfig(platform, configKey, value, userProfile?.id);
      setConfigs((prev) => ({
        ...prev,
        [platform]: {
          ...(prev?.[platform] || {}),
          [configKey]: value
        }
      }));
      setSaveMessage({ type: 'success', text: 'Settings saved successfully' });
    } catch (err) {
      setSaveMessage({ type: 'error', text: 'Failed to save settings' });
    } finally {
      setSaving(false);
      setTimeout(() => setSaveMessage(null), 3000);
    }
  };

  return (
    <MainLayout>
      <div className="max-w-5xl mx-auto px-4 py-6">
        <ConfigHeader saveMessage={saveMessage} />

        {/* Tabs */}
        <div className="flex gap-1 bg-muted p-1 rounded-xl mb-6">
          {TABS?.map((tab) => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                activeTab === tab?.id
                  ? 'bg-card text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <svg className="w-4 h-4 hidden sm:block" viewBox="0 0 24 24" fill="currentColor">
                <path d={tab?.icon} />
              </svg>
              {tab?.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {activeTab === 'pwa' && (
              <PWASettingsTab
                settings={configs?.global?.pwa_settings || {}}
                onSave={(value) => handleSave('global', 'pwa_settings', value)}
                saving={saving}
              />
            )}
            {(activeTab === 'ios' || activeTab === 'android') && (
              <PlatformConfigTab
                platform={activeTab}
                screenVisibility={configs?.[activeTab]?.screen_visibility || {}}
                platformSettings={configs?.[activeTab]?.platform_settings || {}}
                onSaveVisibility={(value) => handleSave(activeTab, 'screen_visibility', value)}
                onSavePlatformSettings={(value) => handleSave(activeTab, 'platform_settings', value)}
                saving={saving}
              />
            )}
          </>
        )}
      </div>
    </MainLayout>
  );
};

export default MobileAppConfiguration;
